import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Invoice } from 'types';

interface UseInvoiceNavigationOptions {
  getCurrentSearchParams: () => string;
}

export interface UseInvoiceNavigationResult {
  navigateToNew: () => void;
  navigateToDetail: (id: number) => void;
  navigateToEdit: (id: number) => void;
  navigateToClone: (invoice: Invoice) => void;
  buildUrl: (basePath: string) => string;
}

/**
 * Hook for navigation within the invoice module.
 * 
 * Provides navigation functions that preserve list state (filters, sorting, pagination)
 * via the `from` URL parameter.
 * 
 * Note: We intentionally do NOT pass invoice data via location state because:
 * - InvoiceDetail and InvoiceForm always fetch fresh data from the API
 * - This ensures data consistency and prevents stale data issues
 * - The only exception is cloneFrom for the clone feature
 */
export function useInvoiceNavigation(
  options: UseInvoiceNavigationOptions
): UseInvoiceNavigationResult {
  const navigate = useNavigate();
  const { getCurrentSearchParams } = options;

  const buildUrl = useCallback((basePath: string): string => {
    const params = getCurrentSearchParams();
    return params ? `${basePath}?from=${encodeURIComponent(params)}` : basePath;
  }, [getCurrentSearchParams]);

  const navigateToNew = useCallback(() => {
    navigate(buildUrl('/invoices/new'));
  }, [navigate, buildUrl]);

  const navigateToDetail = useCallback((id: number) => {
    // Navigate without state - InvoiceDetail always fetches fresh data
    navigate(buildUrl(`/invoices/${id}`));
  }, [navigate, buildUrl]);

  const navigateToEdit = useCallback((id: number) => {
    // Navigate without state - InvoiceForm always fetches fresh data
    navigate(buildUrl(`/invoices/${id}/edit`));
  }, [navigate, buildUrl]);

  const navigateToClone = useCallback((invoice: Invoice) => {
    // Clone is the only case where we pass state - it's used to pre-populate the form
    navigate(buildUrl('/invoices/new'), {
      state: { cloneFrom: invoice }
    });
  }, [navigate, buildUrl]);

  return useMemo(() => ({
    navigateToNew,
    navigateToDetail,
    navigateToEdit,
    navigateToClone,
    buildUrl,
  }), [navigateToNew, navigateToDetail, navigateToEdit, navigateToClone, buildUrl]);
}

export default useInvoiceNavigation;
